import { createBrowserRouter, Navigate } from "react-router-dom";
import { PATH } from "./utils/pagePath";
import ProtectedRoute from "./auth/ProtectedRoute";
import { lazy } from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";


//Import Lazy Loading Pages Here
function ErrorFallback({ error }: any) {
  const { resetBoundary } = useErrorBoundary();
 

  return (
    <div role="alert">
      <p>Something went wrong. Please try again or contact support.</p>
      <button onClick={resetBoundary}>Try again</button>
    </div>
  );
}

const LazyPostLogin = lazy(() => import("./layouts/MainLayout/Layout"));
const LazyLogin = lazy(() => import("./pages/Login/Login"));
const LazyDashboard = lazy(() => import("./pages/Dashboard"));
const LazyUserRoleMaster = lazy(
  () => import("./pages/Users/UserRoleMaster/UserRoleMaster")
);
const LazySkillsMaster = lazy(
  () => import("./pages/Users/SkillsMaster/SkillsMaster")
);
const LazyUserMaster = lazy(
  () => import("./pages/Users/UserMaster/UserMaster")
);
const LazyUserRoleRights = lazy(
  () => import("./pages/Users/UserRoleRights/UserRoleRights")
);
const LazyUserSkillMaster = lazy(
  () => import("./pages/Users/UserSkillMaster/UserSkillMaster")
);
const LazyAddBuilding = lazy(
  () => import("./pages/Building/BuildingSetup/AddBuilding")
);
const LazyBuildingSetUp = lazy(
  () => import("./pages/Building/BuildingSetup/BuildingSet")
);
const LazyAddLocation = lazy(
  () => import("./pages/Building/BuildingSetup/AddLocation")
);
const LazyLocationTypeMaster = lazy(
  () => import("./pages/Building/LocationType/LocationTypeMaster")
);

const LazySeverityMaster = lazy(
  () => import("./pages/Configurations/SeverityMaster/SeverityMaster")
);
const LazyConfigCredentialMaster = lazy(
  () => import("./pages/Configurations/CredentialConfig/CredentialMaster")
);

const LazySaveNumberRangeConfig = lazy(
  () =>
    import("./pages/Configurations/SaveNumberRangeConfig/SaveNumberRangeConfig")
);

const LazyLanguageChange = lazy(
  () => import("./pages/Users/LanguageChange/UserLanguageChange")
);
const LazyMakeMaster = lazy(
  () => import("./pages/AssetAndParts/MakeMaster/MakeMaster")
);
const LazyTaskMaster = lazy(
  () => import("./pages/AssetAndParts/TaskMaster/TaskMaster")
);
const LazyAssetModelMaster = lazy(
  () => import("./pages/AssetAndParts/ModelMaster/ModelMaster")
);
const LazyAssetGroupMaster = lazy(
  () => import("./pages/AssetAndParts/AssetGroupMaster/AssetGroupMaster")
);
const LazyAssetTypeMaster = lazy(
  () => import("./pages/AssetAndParts/AssetTypeMaster/AssetTypeMaster")
);
const LazyServiceTypeMaster = lazy(
  () => import("./pages/AssetAndParts/ServiceTypeMaster/ServiceTypeMaster")
);
const LazyUomMaster = lazy(
  () => import("./pages/AssetAndParts/UomMaster/UomMaster")
);
const LazyWorkOrderType = lazy(
  () => import("./pages/Configurations/WorkOrderType/WorkOrderType")
);
const LazyWorkOrderStatus = lazy(
  () => import("./pages/Configurations/WorkOrderStatus/WorkOrderStatus")
);
const LazyCurrentStatsConfig = lazy(
  () => import("./pages/Configurations/CurrentStatusConfig/CurrentStatusConfig")
);
const LazyStoreMaster = lazy(
  () => import("./pages/Inventory/StoreMaster/StoreMaster")
);
const LazyRackMaster = lazy(
  () => import("./pages/Inventory/RackMaster/RackMaster")
);
const LazyAssetMaster = lazy(
  () => import("./pages/AssetAndParts/AssetMaster/AssetMaster")
);
const LazyPartMaster = lazy(
  () => import("./pages/AssetAndParts/PartMaster/PartMaster")
);
const LazyWeekOFMaster = lazy(
  () => import("./pages/Workforce/WeekOfMaster/WeekOfMaster")
);
const LazyAssetMasterConfiguration = lazy(
  () =>
    import(
      "./pages/Configurations/AssetMasterConfiguration/AssetMasterConfiguration"
    )
);
const LazyReqDescriptionMaster = lazy(
  () =>
    import("./pages/Maintenance/RequestDescription/RequestDescriptionMaster")
);

const LazyWorkorderMasterMaster = lazy(
  () => import("./pages/Helpdesk/Workorder/WorkorderMaster")
);
const LazyVendorMaster = lazy(
  () => import("./pages/Inventory/VendorMaster/VendorMaster")
);

const LazyVendorManagement = lazy(
  () => import("./pages/Inventory/VendorMaster/VendorManagement")
);
const LazyVendorSorComparision = lazy(
  () => import("./pages/Inventory/VendorMaster/VendorSorCompare")
);
const LazyServiceMaster = lazy(
  () => import("./pages/AssetAndParts/ServiceMaster/ServiceMaster")
);
const LazyTeamMaster = lazy(
  () => import("./pages/Workforce/TeamMaster/TeamMaster")
);
const LazyEventMaster = lazy(
  () => import("./pages/Helpdesk/Event/EventMaster")
);
const LazyEscalationMatrix = lazy(
  () => import("./pages/Helpdesk/EscalationMatrix/EscalationMatrix")
);
const LazyPPMSchedule = lazy(
  () => import("./pages/Maintenance/PPMSchedule/PPMSchedule")
);
const LazyInfraPPMSchedule = lazy(
  () => import("./pages/Maintenance/PPMSchedule/InfraPPMSchedule")
);
const LazyMaterialRequest = lazy(
  () => import("./pages/Inventory/MaterialRequest/MaterialRequest")
);
const LazyIssueMaterial = lazy(
  () => import("./pages/Inventory/IssueMaterial/IssueMaterial")
);
const LazyReturnMaterial = lazy(
  () => import("./pages/Inventory/ReturnMaterial/ReturnMaterial")
);
const LazyStoreToStore = lazy(
  () => import("./pages/Inventory/StoreToStore/StoreToStoreMaster")
);
const LazyServiceRequest = lazy(
  () => import("./pages/Helpdesk/ServiceRequest/ServiceRequest")
);
const LazyInventoryMaster = lazy(
  () => import("./pages/Inventory/InventoryMaster/InventoryMaster")
);
const LazyPpmScheduleDetails = lazy(
  () => import("./pages/Maintenance/PPMSchedule/PPMScheduleDetails")
);

const LazyInfraPpmScheduleDetails = lazy(
  () => import("./pages/Maintenance/PPMSchedule/InfraPPmScheduleDetails")
);
const LazyAssetScheduleMaster = lazy(
  () => import("./pages/AssetAndParts/AssetScheduleMaster/AssetScheduleMaster")
);
const LazyMaterialRequestProvision = lazy(
  () =>
    import(
      "./pages/Inventory/MaterailRequestProvision/MaterialRequestProvision"
    )
);
const LazyAnalyticsPlateformAssetLIST = lazy(
  () =>
    import(
      "./pages/AssetAndParts/AnalyticsPlateformAssetLink/AnalyticsPlateformAssetList"
    )
);
const LazyRectifiedCommentMaster = lazy(
  () =>
    import("./pages/Configurations/RectifyCommentMaster/RectifyCommentMaster")
);

const LazyAnanlyticsFddMaster = lazy(
  () => import("./pages/AssetAndParts/AnanlyticsFddMaster/AnanlyticsFddMaster")
);

const LazyReasonMaster = lazy(
  () => import("./pages/Configurations/ReasonMaster/ReasonMaster")
);

const LazyLogDetails = lazy(
  () => import("./pages/Report/LogDetails/LogDetails")
);

const LazyPageNotFound = lazy(
  () => import("./pages/ErrorComponenet/ErrorPage")
);

const LazyInfraAssetSchedule = lazy(
  () => import("./components/pageComponents/AssetSchedule/InfraAssetSchedule")
);

const LazyAssetHierarchyMaster = lazy(
  () =>
    import(
      "./pages/AssetAndParts/AssetHierarchyMaster/AssetHierarchyMaster"
    )
);
const LazyAssetHierarchyMasterForm = lazy(
  () =>
    import(
      "./pages/AssetAndParts/AssetHierarchyMaster/AssetHierarchyMasterForm"
    )
);
const LazyInfraServiceRequestForm = lazy(
  () => import("./pages/Helpdesk/ServiceRequest/InfraServiceRequest")
);

const LazyActionDetailsMaster = lazy(
  () => import("./pages/Configurations/ActionMaster/ActionMaster")
);
const LazyActionMaster = lazy(
  () => import("./pages/Users/UserActionMaster/UserActionMaster")
);

const LazyPdfReport = lazy(() =>
  import(
    "./pages/Report/PdfReport/PdfReport"));

const LazyReport = lazy(() =>
  import("./pages/Report/ReportList/Report"));

const LazyReportTemplate = lazy(() =>
  import("./pages/Report/ReportTempate/ReportTemplate"));


const LazyMaintanceBanner = lazy(
  () => import("./pages/Configurations/MaintanceBannerConfiguration/MaintanceBannerConfiguration")
);

const LazyArchivedWorkOrder = lazy(
  () => import("./pages/Helpdesk/Workorder/ArchivedWorkOrder")
);

//Add Custom Paths Here
export const router = createBrowserRouter(
  [
    {
      path: PATH.DEFAULT,
      element: <Navigate to={PATH.LOGIN} replace />,
    },
    {
      path: PATH.LOGIN,
      element: <LazyLogin />,
    },
    {
      path: PATH.PAGE_NOT_FOUND,
      element: <LazyPageNotFound />,
    },
    {
      element: (
        <ProtectedRoute >
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <LazyPostLogin />
          </ErrorBoundary>
        </ProtectedRoute>
      ),
      children: [
        {
          path: PATH.ARCHIVED_WORK_ORDER,
          element: <LazyArchivedWorkOrder />,
        },
        {
          path: PATH.DASHBOARD,
          element: (<LazyDashboard />),
          //  element:<LazyDashboard />
        },
        {
          path: PATH.USERROLEMASTER,
          element: (
            <LazyUserRoleMaster />
          ),
        },
        {
          path: PATH.SKILLMASTER,
          element: (<LazySkillsMaster />
          ),

        },
        {
          path: PATH.USERMASTER,
          element: (<LazyUserMaster />),
        },
        {
          path: PATH.USERROLE_RIGHT,
          element: (<LazyUserRoleRights />),
        },
        {
          path: `${PATH.USERSKILLMSATER}`,
          element: (<LazyUserSkillMaster />),
        },
        {
          path: PATH.LANGUAGECHANGE,
          element: (<LazyLanguageChange />),
        },
        //Building
        {
          path: PATH.LOCATIONTYPE,
          element: (<LazyLocationTypeMaster />),
        },
        {
          path: PATH.ADDBUILDING,
          element: (<LazyAddBuilding />),
        },
        {
          path: PATH.BUILDINGSETUP,
          element: (<LazyBuildingSetUp />),
        },
        {
          path: PATH.ADDLOCATION,
          element: (<LazyAddLocation />),
        },

        // Asset
        {
          path: PATH.MAKEMASTER,
          element: (
            <LazyMakeMaster />),
        },
        {
          path: PATH.TASKMASTER,
          element: (<LazyTaskMaster />),
        },
        {
          path: PATH.MODELMASTER,
          element: (<LazyAssetModelMaster />),
        },
        {
          path: PATH.ASSETGROUPMASTER,
          element: (<LazyAssetGroupMaster />),
        },
        {
          path: PATH.SERVICEGROUPMASTER,
          element: (<LazyAssetGroupMaster />),
        },
        {
          path: PATH.ASSETTYPEMASTER,
          element: (<LazyAssetTypeMaster />),
        },
        {
          path: PATH.SERVICETYPEMASTER,
          element: (<LazyServiceTypeMaster />),
        },
        {
          path: PATH.UOMMASTER,
          element: (<LazyUomMaster />),
        },
        {
          path: PATH.SEVERITYMASTER,
          element: (<LazySeverityMaster />),
        },
        {
          path: PATH.CREDENTIALCONFIGMASTER,
          element: (<LazyConfigCredentialMaster />),
        },

        {
          path: PATH.WORKORDERTYPE,
          element: (<LazyWorkOrderType />),
        },
        {
          path: PATH.WORKORDERSTATUS,
          element: (<LazyWorkOrderStatus />),
        },
        {
          path: PATH.CURRENTSTATUSCONFIG,
          element: (<LazyCurrentStatsConfig />),
        },
        {
          path: PATH.STOREMASTER,
          element: (<LazyStoreMaster />),
        },
        {
          path: PATH.RACKMASTER,
          element: (<LazyRackMaster />),
        },
        {
          path: PATH.ASSETMASTER,
          element: (<LazyAssetMaster />),
        },
        {
          path: PATH.PARTMASTER,
          element: (<LazyPartMaster />),
        },
        {
          path: PATH.WEEKOFMASTER,
          element: (<LazyWeekOFMaster />),
        },
        {
          path: PATH.REQDESCRIPTIONMASTER,
          element: (<LazyReqDescriptionMaster />),
        },
        {
          path: PATH.WORKORDERMASTER,
          element: (<LazyWorkorderMasterMaster />),
        },
        {
          path: PATH.WORKORDERMASTER_DETAILS,
          element: (<LazyWorkorderMasterMaster />),
        },
        {
          path: PATH.SAVENUMBERRANGECONFIG,
          element: (<LazySaveNumberRangeConfig />),
        },
        {
          path: PATH.ASSETMASTERCONFIGURATION,
          element: (<LazyAssetMasterConfiguration />),
        },
        {
          path: PATH.VENDORMASTER,
          element: (<LazyVendorMaster />),
        },

        {
          path: PATH.VENDORMANAGEMENT,
          element: (<LazyVendorManagement />),
        },
        {
          path: PATH.VENDORSORCOMPARISION,
          element: (<LazyVendorSorComparision />),
        },
        {
          path: PATH.SERVICEMASTER,
          element: (<LazyServiceMaster />),
        },
        {
          path: PATH.TEAMMASTER,
          element: (<LazyTeamMaster />),
        },
        {
          path: PATH.EVENTMASTER,
          element: (<LazyEventMaster />),
        },
        {
          path: PATH.ESCALATIONMATRIX,
          element: (<LazyEscalationMatrix />),
        },
        {
          path: PATH.PPMSCHEDULE,
          element: (<LazyPPMSchedule />),
        },
        {
          path: PATH.INFRAPPMSCHEDULE,
          element: (<LazyInfraPPMSchedule />),
        },
        {
          path: PATH.MATERIALREQUEST,
          element: (<LazyMaterialRequest />),
        },
        {
          path: PATH.ISSUEMATERIAL,
          element: (<LazyIssueMaterial />),
        },
        {
          path: PATH.RETURNMATERIAL,
          element: (<LazyReturnMaterial />),
        },
        {
          path: PATH.STORETOSTORE,
          element: (<LazyStoreToStore />),
        },
        {
          path: PATH.SERVICEREQUEST,
          element: (<LazyServiceRequest />),
        },
        {
          path: PATH.INVENTORYMASTER,
          element: (<LazyInventoryMaster />),
        },
        {
          path: PATH.PPMSCHEDULEDETAILS,
          element: (<LazyPpmScheduleDetails />),
        },

        {
          path: PATH.INFR_PPMSCHEDULEDETAILS,
          element: (<LazyInfraPpmScheduleDetails />),
        },
        {
          path: PATH.ASSETSCHEDULEMASTER,
          element: (<LazyAssetScheduleMaster />),
        },
        {
          path: PATH.MATERIALREQUESTPROVISION,
          element: (<LazyMaterialRequestProvision />),
        },
        {
          path: PATH.ANALYTICSPLATEFORMASSETLINK,
          element: (<LazyAnalyticsPlateformAssetLIST />),
        },
        {
          path: PATH.ANANLYTICSFDD,
          element: (<LazyAnanlyticsFddMaster />),
        },
        {
          path: PATH.REASONMASTER,
          element: (<LazyReasonMaster />),
        },
        {
          path: PATH.RECTIFIEDCOMMENT,
          element: (<LazyRectifiedCommentMaster />),
        },
        {
          path: PATH.LOGDETAILS,
          element: (<LazyLogDetails />),
        },
        {
          path: PATH.INFRASCEDULE,
          element: <LazyInfraAssetSchedule />,
        },

        {
          path: PATH.ASSET_HIERARCHY,
          element: <LazyAssetHierarchyMaster />,
        },
        {
          path: PATH.ADD_ASSET_HIERARCHY,
          element: (<LazyAssetHierarchyMasterForm />),
        },
        {
          path: PATH.INFRA_SERVICE_REQUEST,
          element: (<LazyInfraServiceRequestForm />),
        },

        {
          path: PATH.USER_ACTION_DETAILS,
          element: (<LazyActionMaster />),
        },
        {
          path: PATH.ACTION_MASTER,
          element: (<LazyActionDetailsMaster />),
        },
        {
          path: PATH.PDF_REPORT,
          element: <LazyPdfReport />,
        },
        {
          path: PATH.REPORT,
          element: <LazyReport />,
        },

        {
          path: PATH.INFRA_REPORT_TEMPLATE,
          element: (<LazyReportTemplate />),
        },
        {
          path :PATH.MAINTENANCEBANNERCONFIG,
          element:(<LazyMaintanceBanner/>)
        }
      ],
    },

  ],)
